import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase/supabase';
import AdminProducts from '../components/admin/AdminProducts';
import AdminOrders from '../components/admin/AdminOrders';
import AdminCustomers from '../components/admin/AdminCustomers';
import AdminSettings from '../components/admin/AdminSettings';
import AdminReports from '../components/admin/AdminReports';
import AdminAnalytics from '../components/admin/AdminAnalytics';
import AdminBlog from '../components/admin/AdminBlog';
import AdminPromoCodes from '../components/admin/AdminPromoCodes';
import AdminShipping from '../components/admin/AdminShipping';
import AdminWaitlist from '../components/admin/AdminWaitlist';
import { 
  FiBox, FiShoppingBag, FiLogOut, FiHome, 
  FiUsers, FiPieChart, FiTrendingUp, FiSettings, FiEdit3, FiTag,
  FiCheckCircle, FiClock, FiXCircle, FiDollarSign, FiArrowUpRight, FiArrowDownRight,
  FiTruck, FiMail, FiActivity
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    products: 0,
    orders: 0,
    revenue: 0,
    customers: 0,
    orderStatuses: {},
    netRevenue: 0,
    cancelledCount: 0,
    cancelledRevenue: 0,
    paidCount: 0,
    paidRevenue: 0,
    pendingCount: 0,
    pendingRevenue: 0,
    codCount: 0,
    codRevenue: 0,
    deliveredCount: 0,
    deliveredRevenue: 0,
    thisMonthRevenue: 0,
    lastMonthRevenue: 0,
    thisYearRevenue: 0,
    totalDiscounts: 0,
  });

  useEffect(() => {
    let mounted = true;
    const verifyAccess = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (mounted) navigate('/admin/login');
        return;
      }
      
      const { data: profileData } = await supabase.from('profiles').select('role').eq('id', session.user.id).single();
      
      if (profileData?.role !== 'admin') {
        if (mounted) navigate('/admin/login');
      } else {
        if (mounted) {
          fetchStats();
          setIsAuthChecking(false);
        }
      }
    };
    
    verifyAccess();
    return () => { mounted = false; };
  }, [navigate]);

  const fetchStats = async () => {
    const { count: prodCount } = await supabase.from('products').select('*', { count: 'exact', head: true });
    const { count: custCount } = await supabase.from('profiles').select('*', { count: 'exact', head: true });
    
    const { data: orders } = await supabase
      .from('orders')
      .select('total_amount, created_at, status, payment_status, shipping_address, promo_discount')
      .neq('status', 'draft');
    
    if (!orders || orders.length === 0) {
      setStats(prev => ({ ...prev, products: prodCount || 0, customers: custCount || 0 }));
      return;
    }

    const statusCounts = orders.reduce((acc, order) => {
      const s = order.status.toLowerCase();
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});

    const cancelled = orders.filter(o => o.status === 'cancelled');
    const delivered = orders.filter(o => o.status === 'delivered');
    const paid = orders.filter(o => o.payment_status === 'paid' && o.status !== 'cancelled');
    const pending = orders.filter(o => o.payment_status !== 'paid' && o.status !== 'cancelled' && o.shipping_address?.paymentMethod !== 'cod');
    const cod = orders.filter(o => o.shipping_address?.paymentMethod === 'cod' && o.status !== 'cancelled');

    const totalGross = orders.reduce((a, o) => a + o.total_amount, 0);
    const cancelledRevenue = cancelled.reduce((a, o) => a + o.total_amount, 0);
    const netRevenue = totalGross - cancelledRevenue;
    const totalDiscounts = orders.reduce((a, o) => a + (o.promo_discount || 0), 0);

    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    const thisMonthOrders = orders.filter(o => {
      const d = new Date(o.created_at);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear && o.status !== 'cancelled';
    });
    const lastMonthOrders = orders.filter(o => {
      const d = new Date(o.created_at);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear && o.status !== 'cancelled';
    });
    const thisYearOrders = orders.filter(o => {
      const d = new Date(o.created_at);
      return d.getFullYear() === thisYear && o.status !== 'cancelled';
    });

    const thisMonthRevenue = thisMonthOrders.reduce((a, o) => a + o.total_amount, 0);
    const lastMonthRevenue = lastMonthOrders.reduce((a, o) => a + o.total_amount, 0);
    const thisYearRevenue = thisYearOrders.reduce((a, o) => a + o.total_amount, 0);

    setStats({
      products: prodCount || 0,
      customers: custCount || 0,
      orders: orders.length,
      revenue: totalGross,
      netRevenue,
      cancelledCount: cancelled.length,
      cancelledRevenue,
      paidCount: paid.length,
      paidRevenue: paid.reduce((a, o) => a + o.total_amount, 0),
      pendingCount: pending.length,
      pendingRevenue: pending.reduce((a, o) => a + o.total_amount, 0),
      codCount: cod.length,
      codRevenue: cod.reduce((a, o) => a + o.total_amount, 0),
      deliveredCount: delivered.length,
      deliveredRevenue: delivered.reduce((a, o) => a + o.total_amount, 0),
      orderStatuses: statusCounts,
      thisMonthRevenue,
      lastMonthRevenue,
      thisYearRevenue,
      totalDiscounts,
    });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const monthGrowth = stats.lastMonthRevenue > 0
    ? ((stats.thisMonthRevenue - stats.lastMonthRevenue) / stats.lastMonthRevenue * 100).toFixed(1)
    : stats.thisMonthRevenue > 0 ? 100 : 0;

  if (isAuthChecking) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center font-sans text-brand-darkSage">Verifying secure access...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans">
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col sticky top-0 h-screen">
        <div className="p-6 border-b border-gray-100 flex items-center justify-center">
          <img src="https://media-manager-c.questera.ai/greta-media/29d79c4109d1c19c2e75790b8e50872c9a33c9d5ce79f6f43f60effe48b91ada6d4fe9244bdd866a6b5eb503d1d2a9a7/images/aW1hZ2UvcG5n/154eff52dc1f1c00de3fe58bdd4b1694.png" alt="Logo" className="h-8 object-contain" />
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto custom-scrollbar">
          <SidebarLink icon={FiHome} label="Dashboard" active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} />
          <SidebarLink icon={FiUsers} label="Customers" active={activeTab === 'customers'} onClick={() => setActiveTab('customers')} />
          <SidebarLink icon={FiShoppingBag} label="Orders" active={activeTab === 'orders'} onClick={() => setActiveTab('orders')} />
          <SidebarLink icon={FiBox} label="Inventory" active={activeTab === 'products'} onClick={() => setActiveTab('products')} />
          <SidebarLink icon={FiTruck} label="Shipping" active={activeTab === 'shipping'} onClick={() => setActiveTab('shipping')} />
          <SidebarLink icon={FiTag} label="Promo Codes" active={activeTab === 'promos'} onClick={() => setActiveTab('promos')} />
          <SidebarLink icon={FiMail} label="Waitlist" active={activeTab === 'waitlist'} onClick={() => setActiveTab('waitlist')} />
          <SidebarLink icon={FiActivity} label="Analytics" active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')} />
          <SidebarLink icon={FiPieChart} label="Reports" active={activeTab === 'reports'} onClick={() => setActiveTab('reports')} />
          <SidebarLink icon={FiEdit3} label="Blog" active={activeTab === 'blog'} onClick={() => setActiveTab('blog')} />
          <SidebarLink icon={FiSettings} label="Settings" active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} />
        </nav>
        <div className="p-4 border-t border-gray-100">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors">
            <FiLogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <header className="bg-white border-b border-gray-200 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
          <h1 className="text-2xl font-serif text-gray-800 capitalize">{activeTab === 'promos' ? 'Promo Codes' : activeTab.replace('_', ' ')}</h1>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-500">Admin Mode</span>
            <div className="w-8 h-8 bg-brand-gold/20 rounded-full flex items-center justify-center text-brand-gold font-bold">A</div>
          </div>
        </header>

        <div className="p-8">
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              {/* Top Revenue Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                  <p className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">Net Revenue</p>
                  <p className="text-3xl font-serif font-bold text-green-600">Rs. {stats.netRevenue.toFixed(2)}</p>
                  <p className="text-[10px] text-gray-400 mt-1">Excludes cancelled orders</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                  <p className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">This Month</p>
                  <p className="text-3xl font-serif font-bold text-brand-darkSage">Rs. {stats.thisMonthRevenue.toFixed(2)}</p>
                  <div className="flex items-center gap-1 mt-1">
                    {monthGrowth >= 0 ? (
                      <SafeIcon icon={FiArrowUpRight} className="text-green-500" size={14} />
                    ) : (
                      <SafeIcon icon={FiArrowDownRight} className="text-red-500" size={14} />
                    )}
                    <span className={`text-[10px] font-bold ${monthGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {monthGrowth}% vs last month
                    </span>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                  <p className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">This Year</p>
                  <p className="text-3xl font-serif font-bold text-brand-darkSage">Rs. {stats.thisYearRevenue.toFixed(2)}</p>
                  <p className="text-[10px] text-gray-400 mt-1">{new Date().getFullYear()} net earnings</p>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                  <p className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">Total Orders</p>
                  <p className="text-3xl font-serif font-bold text-brand-darkSage">{stats.orders}</p>
                  <p className="text-[10px] text-gray-400 mt-1">{stats.customers} customers, {stats.products} products</p>
                </div>
              </div>

              {/* Payment Breakdown */}
              <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <FiDollarSign className="text-brand-gold w-6 h-6" />
                  <h2 className="text-xl font-serif font-bold text-gray-800">Payment & Order Breakdown</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-4 bg-green-50 rounded-xl text-center border border-green-100">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <SafeIcon icon={FiCheckCircle} className="text-green-600" size={16} />
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-green-600 mb-1">Paid</p>
                    <p className="text-2xl font-bold text-green-700">{stats.paidCount}</p>
                    <p className="text-xs text-green-500 font-semibold mt-0.5">Rs. {stats.paidRevenue.toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-xl text-center border border-yellow-100">
                    <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <SafeIcon icon={FiClock} className="text-yellow-600" size={16} />
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-yellow-600 mb-1">Pending</p>
                    <p className="text-2xl font-bold text-yellow-700">{stats.pendingCount}</p>
                    <p className="text-xs text-yellow-500 font-semibold mt-0.5">Rs. {stats.pendingRevenue.toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-xl text-center border border-orange-100">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <SafeIcon icon={FiDollarSign} className="text-orange-600" size={16} />
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-orange-600 mb-1">COD</p>
                    <p className="text-2xl font-bold text-orange-700">{stats.codCount}</p>
                    <p className="text-xs text-orange-500 font-semibold mt-0.5">Rs. {stats.codRevenue.toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-xl text-center border border-blue-100">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <SafeIcon icon={FiShoppingBag} className="text-blue-600" size={16} />
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-1">Delivered</p>
                    <p className="text-2xl font-bold text-blue-700">{stats.deliveredCount}</p>
                    <p className="text-xs text-blue-500 font-semibold mt-0.5">Rs. {stats.deliveredRevenue.toFixed(2)}</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-xl text-center border border-red-100">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <SafeIcon icon={FiXCircle} className="text-red-600" size={16} />
                    </div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-red-600 mb-1">Cancelled</p>
                    <p className="text-2xl font-bold text-red-700">{stats.cancelledCount}</p>
                    <p className="text-xs text-red-500 font-semibold mt-0.5">Rs. {stats.cancelledRevenue.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Order Status + Sales Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <FiPieChart className="text-brand-gold w-6 h-6" />
                    <h2 className="text-xl font-serif font-bold text-gray-800">Order Status Summary</h2>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {Object.keys(stats.orderStatuses).length > 0 ? (
                      Object.entries(stats.orderStatuses).map(([status, count]) => {
                        const isCancelled = status === 'cancelled';
                        const isDelivered = status === 'delivered';
                        return (
                          <div key={status} className={`p-4 rounded-xl text-center border ${
                            isCancelled ? 'bg-red-50 border-red-100' :
                            isDelivered ? 'bg-green-50 border-green-100' :
                            'bg-gray-50 border-gray-100'
                          }`}>
                            <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${
                              isCancelled ? 'text-red-500' :
                              isDelivered ? 'text-green-600' :
                              'text-gray-500'
                            }`}>{status}</p>
                            <p className={`text-2xl font-bold ${
                              isCancelled ? 'text-red-700' :
                              isDelivered ? 'text-green-700' :
                              'text-brand-darkSage'
                            }`}>{count}</p>
                          </div>
                        );
                      })
                    ) : (
                      <div className="col-span-full py-6 text-center text-sm text-gray-500">No orders have been placed yet.</div>
                    )}
                  </div>
                </div>

                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <FiTrendingUp className="text-brand-gold w-6 h-6" />
                    <h2 className="text-xl font-serif font-bold text-gray-800">Sales Overview</h2>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between p-4 bg-brand-sand/30 rounded-xl border border-brand-darkSage/10">
                      <span className="text-sm font-medium text-brand-darkSage">Avg. Order Value</span>
                      <span className="font-bold text-brand-darkSage">Rs. {(stats.netRevenue / ((stats.orders - stats.cancelledCount) || 1)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between p-4 bg-green-50 rounded-xl border border-green-100">
                      <span className="text-sm font-medium text-green-800">Completion Rate</span>
                      <span className="font-bold text-green-600">
                        {stats.orders > 0 ? Math.round((stats.deliveredCount / (stats.orders - stats.cancelledCount || 1)) * 100) : 0}%
                      </span>
                    </div>
                    <div className="flex justify-between p-4 bg-red-50 rounded-xl border border-red-100">
                      <span className="text-sm font-medium text-red-800">Cancellation Rate</span>
                      <span className="font-bold text-red-600">
                        {stats.orders > 0 ? Math.round((stats.cancelledCount / stats.orders) * 100) : 0}%
                      </span>
                    </div>
                    <div className="flex justify-between p-4 bg-amber-50 rounded-xl border border-amber-100">
                      <span className="text-sm font-medium text-amber-800">Total Discounts</span>
                      <span className="font-bold text-amber-600">Rs. {stats.totalDiscounts.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'customers' && <AdminCustomers />}
          {activeTab === 'products' && <AdminProducts />}
          {activeTab === 'orders' && <AdminOrders />}
          {activeTab === 'shipping' && <AdminShipping />}
          {activeTab === 'promos' && <AdminPromoCodes />}
          {activeTab === 'waitlist' && <AdminWaitlist />}
          {activeTab === 'analytics' && <AdminAnalytics />}
          {activeTab === 'settings' && <AdminSettings />}
          {activeTab === 'reports' && <AdminReports />}
          {activeTab === 'blog' && <AdminBlog />}
        </div>
      </main>
    </div>
  );
};

const SidebarLink = ({ icon: Icon, label, active, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
      active ? 'bg-brand-darkSage text-white shadow-lg' : 'text-gray-600 hover:bg-gray-100'
    }`}
  >
    <Icon size={18} className={active ? 'text-brand-gold' : 'text-gray-400'} />
    {label}
  </button>
);

export default Admin;